import sleep from 'lib/sleep'
import Cookies from 'js-cookie'
import posthog from 'posthog-js'

const FEATURE_FLAG_TIMEOUT = 5000

// Only enable posthog if it's set in the config.
const CLIENT_SIDE = typeof window !== 'undefined'
export const POSTHOG_ENABLED = CLIENT_SIDE && CONFIG.posthog.enabled
const POSTHOG_SESSION_REC_ENABLED = CLIENT_SIDE && CONFIG.posthog.sessionRecordingEnabled

let posthogLoaded = false
let cachedOrgLicense = null

//
// Hack to wait for PostHog to initialize (via the `loaded` callback).
// The library is imported statically, but initialization is asynchronous.
// This waits for the `posthogLoaded` flag to be set after initialization.
//
// Ideally there would be some higher level saga that handled both I think.
//
export const waitForPosthog = async (interval = 1000, maxWait = 10000) => {
  let waited = 0

  while (waited <= maxWait) {
    if (posthogLoaded) {
      return true
    }

    await sleep(interval)
    waited += interval
  }

  console.warn('Posthog failed to load or took over 10 seconds')
  return false
}

const getBootstrap = () => {
  const anonId = Cookies.get('anonId')

  if (anonId) {
    return {
      distinctID: anonId,
      isIdentifiedID: false
    }
  }

  return {}
}

/**
 * Initialize posthog client
 */
export const initializePostHog = async cookiePreference => {
  const gpcValue = window.navigator.globalPrivacyControl

  if (!POSTHOG_ENABLED || gpcValue) {
    return
  }

  //
  // Config options here: https://posthog.com/docs/libraries/js#config
  // Reverse proxy setup: https://posthog.com/docs/advanced/proxy/cloudfront
  //

  posthog.init(CONFIG.posthog.key, {
    api_host: CONFIG.posthog.apiHost,
    loaded: function () {
      posthogLoaded = true
    },
    disable_session_recording: true,
    opt_out_capturing_by_default: true,
    cookieless_mode: 'on_reject',
    autocapture: false,
    capture_pageview: true,
    bootstrap: getBootstrap(),
    ui_host: 'https://us.posthog.com',
    defaults: '2025-05-24',
    exception_autocapture: false
  })

  if (
    cookiePreference &&
    typeof cookiePreference === 'string' &&
    (cookiePreference === 'accepted' || cookiePreference.split(',').includes('targeting'))
  ) {
    posthog.opt_in_capturing()
  } else {
    posthog.opt_out_capturing()
  }
}

/**
 * Set the metadata object for a person. Useful for tracking, cohorts, etc.
 * Right now use the user object from loadUser() call.
 * @param {*} user
 * @param organizationLicense
 */
export const setPostHogPersonMetadata = async (user, organizationLicense = null) => {
  if (!user) {
    return
  }

  if (!posthogLoaded) {
    const didLoad = await waitForPosthog()
    if (!didLoad) {
      return
    }
  }

  // Have to set this here so cookieless users can still have org saved
  cachedOrgLicense = user.organizationLicense || organizationLicense

  if (posthog.has_opted_out_capturing()) {
    return
  }

  if (user?.email) {
    posthog.identify(user.email)
  }

  posthog.people.set({
    email: user.email,
    plan: user.subscription?.plan || 'free',
    subscriptionStatus: user.subscription?.status || 'inactive',
    admin: user.admin,
    expert: user.expert,
    orcid: user.orcid,
    signupType: user.signupType,
    organizationLicense: user.organizationLicense,
    organizationLicenseStatus: cachedOrgLicense?.status,
    persona: user.persona || user.userSpecifiedPersona || null
  })
}

/**
 * Reset posthog. Call this when someone logs out. Important so it
 *    unsets any distinct_ids, which matters for users that share computers.
 * The optional resetDeviceId parameter can be used to reset the device ID.
 * @param {*} resetDeviceId
 */
export const resetPostHog = (resetDeviceId = false) => {
  if (!posthogLoaded) {
    return
  }

  cachedOrgLicense = null
  posthog.reset(resetDeviceId)
}

/**
 * Check if user has opted out of capturing
 * Only runs if posthog is initialize because the library
 *   underneath will crash if not -_-. Bug in their code.
 */
export const hasOptedOutCapturing = () => {
  if (!posthogLoaded) {
    return null
  }

  return posthog.has_opted_out_capturing()
}

/**
 * @param {Object} eventData Posthog event data
 * @param {string} eventData.name Event name (string)
 * @param {Object} [eventData.metadata] Metadata object, e.g. {property1: 'value', property2: 'another value'}
 */
export const capturePostHogEvent = eventData => {
  if (!posthogLoaded) {
    return
  }

  const metadataFromEvent = eventData?.metadata || {}
  const metadata = {
    organizationLicense: cachedOrgLicense,
    ...metadataFromEvent
  }

  try {
    posthog.capture(eventData.name, metadata)
  } catch (e) {
    console.error(e)
  }
}

/**
 * So, if someone is opted out of tracking, when they login, posthog won't sent their metadata
 *    because those are event calls.
 * This means that if opted out, it won't be able to set feature flags based on your property, since those
 *    are unset for opted out users.
 * Has an optional override (enableIfOptedOut) which, when set to true, will always return true if
 *    a user is opted out for a feature flag.
 * Has an optional override (enableForAdmin) which, when set to true, will always return true if
 *    a user is a admin.
 * NOTE: a user being an admin is something we track in the app, not in posthog, because all scite admins
 *    are opted out of posthog tracking.
 */
export const isFeatureFlagEnabled = async ({
  featureFlag,
  user = null,
  enableForAdmin = true,
  enableIfOptedOut = false
}) => {
  if (!(await waitForPosthog())) {
    return false
  }

  try {
    if (enableForAdmin && user?.admin) {
      return true
    }

    if (enableIfOptedOut && hasOptedOutCapturing()) {
      return true
    }

    if (user?.email) {
      // Set person property in posthog in case email was incorrectly set before
      posthog.setPersonPropertiesForFlags({ email: user.email })
    }

    // We need to wait for feature flags to be loaded
    await Promise.race([
      new Promise(resolve => posthog.onFeatureFlags(resolve)),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error('Feature flags timeout')), FEATURE_FLAG_TIMEOUT)
      )
    ])

    return posthog.isFeatureEnabled(featureFlag)
  } catch (e) {
    console.error(e)
  }
}

export const getFeatureFlag = async featureFlag => {
  if (!(await waitForPosthog())) {
    return
  }

  try {
    // We need to wait for feature flags to be loaded
    await Promise.race([
      new Promise(resolve => posthog.onFeatureFlags(resolve)),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error('Feature flags timeout')), FEATURE_FLAG_TIMEOUT)
      )
    ])
    const variantKey = posthog.getFeatureFlag(featureFlag)
    return variantKey
  } catch (e) {
    console.error(e)
  }
}

export const getFeatureFlagPayload = async featureFlag => {
  if (!(await waitForPosthog())) {
    return
  }

  try {
    // We need to wait for feature flags to be loaded
    await Promise.race([
      new Promise(resolve => posthog.onFeatureFlags(resolve)),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error('Feature flags timeout')), FEATURE_FLAG_TIMEOUT)
      )
    ])
    const payload = posthog.getFeatureFlagPayload(featureFlag)
    return payload
  } catch (e) {
    console.error(e)
  }
}

export const startPosthogSessionRecording = async () => {
  if (!posthogLoaded) {
    const didLoad = await waitForPosthog()
    if (!didLoad) {
      return
    }
  }

  if (!POSTHOG_SESSION_REC_ENABLED) {
    return
  }

  posthog.startSessionRecording()
}

export const stopPosthogSessionRecording = () => {
  if (!posthogLoaded) {
    return
  }

  if (!POSTHOG_SESSION_REC_ENABLED) {
    return
  }

  posthog.stopSessionRecording()
}
